import React, { useCallback } from 'react';

import Box from '@mui/material/Box';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Collapse from '@mui/material/Collapse';
import { useDrag, DragPreviewImage } from 'react-dnd';
import useDoubleClick from './useDoubleClick';
import { useNewWorklistToggleContext } from '@worklist-2/core/src/context/NewWorklistToggleContext';
import { WORKLIST_SPACING } from '@rs-ui/helpers/constants';

const STYLE_EXCEPTION_COLUMNS_IDS = ['studyStatus', 'readingPhysician'];

const DataGridRow = ({
	tableRow,
	rowIndex,
	enableRowDnd = false,
	flexRender,
	selectedRow,
	cellsPadding,
	isExpanded,
	subRow,
	cellSx,
	onRowClick,
	onRowDoubleClick,
	visitedWorklist,
	size,
	onContextMenuClick,
}) => {
	const [, dragRowRef, preview] = useDrag({
		type: 'ROW',
		item: tableRow.original,
	});
	const { toggleGetFeatureFlagValue } = useNewWorklistToggleContext();

	const mavenWorklistUsabilityImprovements = toggleGetFeatureFlagValue('maven-2686-new-worklist-toggle');
	const mavenWorklistVerticalSpacing = mavenWorklistUsabilityImprovements
		? WORKLIST_SPACING.verticalMin
		: WORKLIST_SPACING.verticalMax;
	const mavenWorklistHorizontalSpacing = mavenWorklistUsabilityImprovements
		? WORKLIST_SPACING.horizonatalMin
		: WORKLIST_SPACING.horizonatalMax;
	const mavenEnableWorklistContextMenu = toggleGetFeatureFlagValue('maven-2735-worklist-right-click-menu');

	// This function is used to generate a unique key for each cell in the row
	const getCellKey = useCallback(
		cell => (tableRow.original?.id ? `${tableRow.original.id}-${cell.column.id}` : `${cell.id}`),
		[tableRow]
	);

	/**
	 * Check click state
	 */
	const isDoubleClickOnRow = useDoubleClick(300);

	return (
		<>
			<DragPreviewImage
				connect={preview}
				src="data:image/png;base64,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"
			/>
			<TableRow
				ref={enableRowDnd ? dragRowRef : null}
				data-cy={`study-status-row-${tableRow.id}`}
				data-testid={`study-status-row-${tableRow.id}`}
				sx={{
					'&:hover .hover': {
						visibility: 'visible',
					},
					'&:hover #icons': {
						backgroundColor: '#333333',
					},
					'&.Mui-selected, &.Mui-selected:hover': {
						backgroundColor: '#2727274a !important',
					},
					'& h6': {
						fontWeight: 'normal',
					},
					'&': {
						backgroundColor: mavenWorklistUsabilityImprovements
							? `${
									visitedWorklist === tableRow.original.id
										? '#2727276a'
										: (parseInt(tableRow.id, 10) + 2) % 2 === 0
										? '#393939'
										: '#414141'
							  }`
							: `${visitedWorklist === tableRow.original.id && '#2727276a'}`,
					},
					height: `${size}px`,
				}}
				// Need to check for 0 explicitly
				selected={selectedRow}
				onClick={async e => {
					if (!enableRowDnd && !!window.getSelection().toString()) {
						return;
					}
					if (await isDoubleClickOnRow()) {
						onRowDoubleClick?.(tableRow.original, rowIndex, e);
					} else if (onRowClick) {
						onRowClick(tableRow.original, rowIndex, e);
					}
				}}
				onContextMenu={e => {
					onContextMenuClick(e, tableRow.original, rowIndex);
				}}
				onDoubleClick={e => {
					onRowDoubleClick?.(tableRow.original, rowIndex, e);
				}}
			>
				{tableRow.getVisibleCells().map(
					cell =>
						cell.column.columnDef.visible && (
							<TableCell
								key={getCellKey(cell)}
								data-cy={`study-status-cell-${cell.id}`}
								data-testid={`study-status-cell-${cell.id}`}
								id={cell.column.id}
								sx={{
									padding: STYLE_EXCEPTION_COLUMNS_IDS.includes(cell.column.id)
										? `${mavenWorklistVerticalSpacing}px ${mavenWorklistHorizontalSpacing}px`
										: cellsPadding ||
										  `${mavenWorklistVerticalSpacing}px ${mavenWorklistHorizontalSpacing}px`,
									position:
										cell.column.id === 'studyStatus'
											? 'relative'
											: cell.column.id === 'icons'
											? 'sticky'
											: undefined,
									right: cell.column.id === 'icons' ? '0' : undefined,
									paddingRight: cell.column.id === 'icons' ? '15px' : undefined,
									borderBottom: '0px',
									whiteSpace: 'nowrap',
									width: cell.column.getSize(),
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									...cellSx,
								}}
							>
								{flexRender(cell.column.columnDef.cell, {
									...cell.getContext(),
									renderText: mavenEnableWorklistContextMenu,
								})}
							</TableCell>
						)
				)}
			</TableRow>
			{subRow && (
				<TableRow
					sx={{
						'& .MuiTableCell-root': {
							borderBottom: 'none',
						},
					}}
				>
					<TableCell colSpan={12} style={{ padding: 0 }}>
						<Collapse unmountOnExit in={isExpanded} timeout="auto">
							<Box>{subRow}</Box>
						</Collapse>
					</TableCell>
				</TableRow>
			)}
		</>
	);
};

export default React.memo(DataGridRow);
